// Everything that changes when the arcade is deployed lives here.
export const CONFIG = {
  // onyx-1 is the current testnet. pearl-1's RPC host stopped resolving
  // (getaddrinfo ENOTFOUND rpc.pearl.testnets.gno.land, 2026-09-30), so the
  // chain the earlier proofs ran against no longer exists; onyx-1 answered at
  // block 57,358 the same day.
  chainId: 'onyx-1',
  rpc: 'https://rpc.onyx.testnets.gno.land:443',
  gnoweb: 'https://onyx.testnets.gno.land',

  // Realm paths. REDEPLOYED 2026-10-08 -- the duel was rebuilt from scratch
  // (no secret, no nonce, no commit/reveal, no forfeit: Post/Cancel/Accept/
  // Resolve/FlushPot/SweepStuckRake, settled by the beacon's randomness at a
  // round pinned on Accept) and every one of these four paths moved to a
  // versioned slot (.../v3, .../v2) as part of that redeploy. Confirmed live
  // on onyx-1 by querying vm/qfuncs against each path directly (see this
  // change's own verification, not a file in this repo) -- all five realms
  // mentioned in the redeploy (duel, raffle, speed, beacon, plus `ops`, which
  // this front end does not call) answered.
  //
  // THE ADDRESS IN THESE PATHS IS NOT THE PROJECT'S NAME AND IS NOT PERMANENT.
  // r/sys/names is enabled on onyx and username registration is PAUSED chain-wide
  // pending GovDAO, so "splash" cannot be registered by anyone today. The deploy
  // key's own address namespace is the only one open. Every one of these lines
  // changes back the day a real namespace exists -- which is also the day the
  // house's vault address moves, because it is derived from a package path.
  duelRealm: 'gno.land/r/g1r4aw43kagaaquhegxw6xr346h9f2qu2ezf0jar/arcade/duel/v4',
  raffleRealm: 'gno.land/r/g1r4aw43kagaaquhegxw6xr346h9f2qu2ezf0jar/arcade/raffle/v4',
  speedRealm: 'gno.land/r/g1r4aw43kagaaquhegxw6xr346h9f2qu2ezf0jar/arcade/speed/v2',
  beaconRealm: 'gno.land/r/g1r4aw43kagaaquhegxw6xr346h9f2qu2ezf0jar/beacon/v2',
  // The room. Deployed 2026-10-08 at height 258,984 and proven by posting to
  // it. NOT YET READ BY ANY PAGE -- the realm exists and the UI does not, so
  // this line is wiring waiting for a consumer, not a live dependency.
  chatRealm: 'gno.land/r/g1r4aw43kagaaquhegxw6xr346h9f2qu2ezf0jar/chat/v1',
  wugnotRealm: 'gno.land/r/gnoland/wugnot',

  // 1 GNOT = 1e6 ugnot. wugnot on pearl-1 reported decimals 0 in its GRC20
  // metadata but wraps ugnot 1:1, so the display divisor is still 1e6.
  ugnotPerGnot: 1_000_000n,
  rakeBps: 300n,

  // The most IDs one OfferIDs/DuelIDs call returns (duel.gno PageLimit). The
  // realm clamps a larger limit; we never ask for more than one page. The
  // raffle's Weeks and the speed realm's Windows have the same PageLimit.
  pageLimit: 100,

  // Beacon cadence, for the wait copy only (beacon/SPEC.md: 5 minutes).
  beaconCadenceSec: 300,

  // Gas is sized by simulation before every send (sign.js estimateFee).
  // gasPrice is ugnot per 1000 gas; pearl-1's floor was 1. This fee is only
  // the fallback for a wallet extension that wants a number up front:
  // wugnot.Approve measured 2.0M gas master-signed, 6.0M session-signed.
  gasPrice: 2n,
  fee: { gas_wanted: 12_000_000n, gas_fee: '120000ugnot' },

  // Session key: 24h, scoped to the realms the page calls, with enough
  // ugnot for ~30 transactions' gas fees.
  session: { ttlSec: 24 * 3600, spendLimit: '10000000ugnot' },

  pollMs: 6000,

  // --- the two draw games --------------------------------------------------
  //
  // EVERYTHING IN HERE THAT THE REALMS EXPORT IS READ FROM THE REALM AT BOOT
  // AND THESE VALUES ARE ONLY THE FALLBACK for a chain where the realm is not
  // deployed (see chain/draws.js economics()). The ones the realms do NOT
  // export are marked MUST TRACK THE CONTRACTS: nothing on-chain will tell the
  // page when one of them changes, so a contract edit has to change this file
  // in the same commit.
  draw: {
    // Exported by the realms as consts — read live when they are up.
    //   raffle.gno  TicketPrice int64 = 10_000_000   (10 GNOT)
    //   raffle.gno  EntryUnitPrice int64 = 250_000   (0.25 GNOT; 40 per ticket)
    //   speed.gno   TicketPrice int64 = 1_000_000    (1 GNOT)
    raffleTicketPrice: 10_000_000n,
    entryUnitPrice: 250_000n,
    speedTicketPrice: 1_000_000n,

    // Exported by speed.gno as WindowBlocks() — read live when it is up.
    // 26182 / 24 in Go integer division = 1090 blocks, ~1 hour.
    speedWindowBlocks: 1090n,

    // Derived from raffle.gno's exported boundary when the realm is up:
    // WeekEndHeight(1) - WeekEndHeight(0), which is what that function's own
    // doc comment says to use ("the week's length is WeekEndHeight(1) -
    // WeekEndHeight(0), so one function answers every question the constant
    // would have"). blocksPerDay = 26182 and raffleWeekDays = 7 are still
    // unexported, so this stays the FALLBACK for a chain without the realm.
    raffleWeekBlocks: 183_274n,

    // MUST TRACK THE CONTRACTS — houseCutBps = 500 in both realms, i.e. 5% of
    // every ticket, escrowed per draw and earned only by a draw that runs.
    houseCutBps: 500n,

    // BLOCKS -> SECONDS IS THIS PAGE'S JOB AND IT IS PER CHAIN. The realms
    // return a block HEIGHT and deliberately never a time: WeekEndHeight's own
    // comment says a caller wanting a wall-clock countdown "must multiply by a
    // block time IT chooses and own the error", because nothing on chain knows
    // how fast the chain it is running on actually produces blocks.
    //
    // MEASURED, per chain id. These are observations, not the contracts' figure:
    //   gno.land  3.306 s  (mainnet, measured 2026-09-28)
    //   onyx-1    3.376 s  (measured; quoted in raffle.gno/speed.gno's own
    //                       WeekEndHeight/WindowEndHeight comments)
    blockSeconds: {
      'gno.land': 3.306,
      'onyx-1': 3.376,
    },

    // The CONTRACTS' assumption, 86400/blocksPerDay = 86400/26182 = 3.29998 s,
    // and the only reason it is here is so a chain nobody has measured gets a
    // number that is LABELLED an assumption rather than passed off as a
    // measurement (src/draw.js blockSecondsFor -> source 'assumed'). It is not
    // the figure any chain in the table above runs at: over a 183,274-block
    // raffle week the gap to onyx-1's measured rate is 3.87 hours, which is
    // exactly why the contracts return a height and not a countdown.
    assumedBlockSeconds: 86_400 / 26_182,
  },
};
